// src/pages/vendas/PesqNotasPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { FaFileExcel, FaFilePdf, FaSortDown, FaSortUp } from 'react-icons/fa';
import { fetchData } from '../../services/Api';
import { useMessage } from '../../contexts/geral/MessageContext';
import { formatCurrency, formatNumeric } from '../../utils/numberUtils';
import { NotaRequest } from '../../models/vendas/NotaRequest';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { getDescricaoTipoMov } from '../../models/estoque/TipoMov.ts';
import { getDescricaoSituacaoNF } from '../../models/vendas/SituacaoNF.ts';
import { usePrinter } from '../../contexts/geral/PrinterContext.js';
import CancelarNFModal from './CancelarNFModal';
import ViewNotaModal from './ViewNotaModal.js';
import Input from '../../components/input/Input.js';
import { useCliente } from '../../contexts/pessoas/ClienteContext.js';
import Select from '../../components/input/Select.js';
import PageContainer from '../../components/geral/PageContainer.js';
import PageHeader from '../../components/geral/PageHeader.js';
import PageFooter from '../../components/geral/PageFooter.js';
import PageContent from '../../components/geral/PageContent.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import { useNavigate } from 'react-router-dom';
import { useNota } from '../../contexts/vendas/NotaContext.js';
import { getDanfseA4PDFByNotaIdService } from '../../services/vendas/PDVService.js';
import { buscarRecebimentosVinculadosNota } from '../../services/vendas/NotaFiscalService.js';
import LazyLoading from '../../components/modalForm/LazyLoading.js';
import {
    gerarRelatorioVendasExcel,
    gerarRelatorioVendasPdf,
} from '../../services/geral/RelatorioGerencialService.js';
import {
    baixarBlob,
    criarNomeArquivo,
    exibirPdfBlob,
    fecharJanela,
    prepararJanelaPdf,
} from '../../utils/ArquivoUtils.js';

const PesqNotasPage = () => {
    const { showMessage } = useMessage();
    const [periodoInicial, setPeriodoInicial] = useState('');
    const [periodoFinal, setPeriodoFinal] = useState('');
    const [horaInicial, setHoraInicial] = useState('00:00');
    const [horaFinal, setHoraFinal] = useState('23:59');
    const [cliente, setCliente] = useState('');
    const [numDoc, setNumDoc] = useState('');
    const [situacao, setSituacao] = useState(-1);
    const [notas, setNotas] = useState([]);
    const [notasSel, setNotasSel] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [notaSelecionada, setNotaSelecionada] = useState(null);
    const [notaIdSelecionada, setNotaIdSelecionada] = useState(null);
    const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
    const [resultadoVersao, setResultadoVersao] = useState(0);
    const [gerandoRelatorio, setGerandoRelatorio] = useState(null);
    const { printNFCePDFById, printReciboPDF } = usePrinter();
    const { showClienteSearch } = useCliente();
    const [disableBtn, setDisableBtn] = useState(false);
    const { showYesNo } = useYesNo();
    const navigate = useNavigate();
    const { buscaNotaById, setNotaSel, deleteNota } = useNota();

    const formatDateToInputValue = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const dataAtual = new Date();
        const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
        const dataAtualFormatada = formatDateToInputValue(dataAtual);
        const primeiroDiaMesFormatado = formatDateToInputValue(primeiroDiaMes);

        setPeriodoInicial(primeiroDiaMesFormatado);
        setPeriodoFinal(dataAtualFormatada);
    }, []);

    useEffect(() => {
        if (periodoInicial && periodoFinal) {
            buscarNotas();
        }
    }, [periodoInicial, periodoFinal]);

    const filtrosAtuais = () => {
        const dataLanIni = `${periodoInicial}T${horaInicial || '00:00'}:00`;
        const dataLanFim = `${periodoFinal}T${horaFinal || '23:59'}:59`;

        return new NotaRequest({
            naturezaId: 1,
            dataLanIni,
            dataLanFim,
            clienteNome: cliente,
            numDoc,
            situacaoNF: situacao,
        });
    };

    const buscarNotas = async () => {
        const filtros = filtrosAtuais();

        const queryString = filtros.toQueryString();

        try {
            setDisableBtn(true);
            const result = await fetchData(`/Nota?${queryString}`);
            setNotas(result.reverse());
            setResultadoVersao((versaoAtual) => versaoAtual + 1);
            setDisableBtn(false);
        } catch (error) {
            showMessage(`Erro ao buscar notas: ${error}`, 'danger');
        }
    };

    const gerarRelatorio = async (formato) => {
        let janelaPdf = null;
        try {
            setGerandoRelatorio(formato);
            if (formato === 'pdf') janelaPdf = prepararJanelaPdf();
            const filtrosPesquisa = filtrosAtuais();
            const filtros = {
                dataLanIni: filtrosPesquisa.dataLanIni,
                dataLanFim: filtrosPesquisa.dataLanFim,
                clienteNome: filtrosPesquisa.clienteNome,
                numDoc: filtrosPesquisa.numDoc,
                situacaoNF: filtrosPesquisa.situacaoNF,
                ordenarPor: ordenacao.campo,
                ordemDescendente: ordenacao.direcao === 'desc',
            };
            const arquivo = formato === 'pdf'
                ? await gerarRelatorioVendasPdf(filtros)
                : await gerarRelatorioVendasExcel(filtros);
            if (formato === 'pdf') {
                exibirPdfBlob(arquivo, janelaPdf);
            } else {
                baixarBlob(arquivo, criarNomeArquivo('relatorio-vendas', 'xlsx'));
            }
        } catch (error) {
            fecharJanela(janelaPdf);
            showMessage(error?.message || 'Não foi possível gerar o relatório de vendas.', 'danger');
        } finally {
            setGerandoRelatorio(null);
        }
    };

    const getTipoAcaoBotao = () => {
        if (notasSel.length === 0) return 'Cancelar NF';

        const todasExcluiveis = notasSel.every(
            (n) => n.situacaoNF === 10 || n.situacaoNF === 3,
        );

        const situacoes = [...new Set(notasSel.map((n) => n.situacaoNF))];
        const tiposDiferentes = situacoes.length > 1;

        if (tiposDiferentes) return 'DESABILITADO';
        if (notasSel.every((n) => n.situacaoNF === 9)) return 'Excluir Recibo';
        if (todasExcluiveis) return 'Excluir NF';

        return 'Cancelar NF';
    };

    const obterAvisoRecebimentosVinculados = async (notasSelecionadas) => {
        const resultados = await Promise.all(
            notasSelecionadas.map(async (nota) => ({
                nota,
                vinculos: await buscarRecebimentosVinculadosNota(nota.id),
            })),
        );
        const avisos = resultados
            .filter(({ vinculos }) => vinculos?.recebimentoIds?.length > 0)
            .map(({ nota, vinculos }) => {
                const contas = vinculos.recebimentoIds.map((id) => `#${id}`).join(', ');
                return `A nota #${nota.id} tem a conta a receber ${contas} vinculada.`;
            });

        if (avisos.length === 0) return null;

        return `${avisos.join(' ')} A conta a receber será excluída antes da nota. Deseja continuar?`;
    };

    const handleAcaoNF = async () => {
        const tipo = getTipoAcaoBotao();

        if (tipo === 'DESABILITADO') {
            showMessage('Selecione notas com a mesma situação para continuar.', 'warning');
            return;
        }

        if (tipo === 'Excluir NF') {
            let avisoRecebimentos;
            try {
                avisoRecebimentos = await obterAvisoRecebimentosVinculados(notasSel);
            } catch (error) {
                showMessage(`Erro ao verificar contas a receber vinculadas: ${error}`, 'danger');
                return;
            }

            const confirmar = await showYesNo(
                avisoRecebimentos || 'Deseja realmente excluir as notas selecionadas?',
            );
            if (!confirmar) return;

            for (const nota of notasSel) {
                await handleExcluirNF(nota);
            }

            buscarNotas();
            setNotasSel([]);
        } else {
            await handleCancelarNF();
        }
    };

    const handleExcluirNF = async (nota) => {
        console.log(`Excluindo nota ${nota.id}...`);
        await deleteNota(nota.id);
    };

    const handleBuscarCliente = async () => {
        const clienteSel = await showClienteSearch();

        if (clienteSel) {
            setCliente(clienteSel.pessoa.nome);
        } else {
            setCliente('');
        }
    };

    const calcularTotal = () => {
        return notas.reduce((acc, nota) => acc + nota.vNota, 0);
    };

    const notasOrdenadas = useMemo(() => {
        const { campo, direcao } = ordenacao;

        if (!campo) {
            return notas;
        }

        const multiplicador = direcao === 'asc' ? 1 : -1;

        return [...notas].sort((a, b) => {
            const valorA = campo === 'tipoMovDescricao'
                ? getDescricaoTipoMov(a.tipoMov)
                : campo === 'caixa'
                    ? a.caixa?.descricao
                    : campo === 'situacaoDescricao'
                        ? getDescricaoSituacaoNF(a.situacaoNF)
                        : campo === 'clienteDescricao'
                            ? `${a.clienteId || ''} - ${a.clienteNome || ''}`
                            : campo === 'hora'
                                ? formatTime(a.dataLanc)
                                : a?.[campo];
            const valorB = campo === 'tipoMovDescricao'
                ? getDescricaoTipoMov(b.tipoMov)
                : campo === 'caixa'
                    ? b.caixa?.descricao
                    : campo === 'situacaoDescricao'
                        ? getDescricaoSituacaoNF(b.situacaoNF)
                        : campo === 'clienteDescricao'
                            ? `${b.clienteId || ''} - ${b.clienteNome || ''}`
                            : campo === 'hora'
                                ? formatTime(b.dataLanc)
                                : b?.[campo];

            if (['id', 'serie', 'numDoc', 'vNota', 'cfop'].includes(campo)) {
                return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
            }

            if (campo === 'dataLanc') {
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            }

            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
                sensitivity: 'base',
            }) * multiplicador;
        });
    }, [notas, ordenacao]);

    const alterarOrdenacao = (campo) => {
        setOrdenacao((atual) => ({
            campo,
            direcao: atual.campo === campo
                ? (atual.direcao === 'asc' ? 'desc' : 'asc')
                : 'asc',
        }));
    };

    const renderOrdenadorColuna = (campo, titulo, style = {}) => {
        const ativa = ordenacao.campo === campo;

        return (
            <button
                type="button"
                onClick={() => alterarOrdenacao(campo)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 'bold',
                    color: 'inherit',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: style.textAlign || 'left',
                }}
            >
                {titulo}
                {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
            </button>
        );
    };

    const handleRadioChange = (nota) => {
        setNotasSel([nota]);
    };

    const handleImprimir = () => {
        notasSel.forEach((nota) => {
            if (nota.modeloNF?.toUpperCase() === 'NFSE' && Number(nota.situacaoNF) === 2) {
                ImprimirNFSeA4(nota.id);
            } else if (nota.modeloNF === '65') {
                //printNFCe(nota.id);
                printNFCePDFById(nota.id);
            } else if (nota.modeloNF === '55') {
                ImprimirNFe(nota.id);
            } else {
                //printRecibo(nota.id);
                printReciboPDF(nota.id);
            }
        });
    };

    async function ImprimirNFSeA4(notaId) {
        try {
            const pdfBlob = await getDanfseA4PDFByNotaIdService({ notaId });
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Falha na requisição:', err);
            alert('Erro na comunicação com o servidor.');
        }
    }

    async function ImprimirNFe(notaId) {
        try {
            const response = await fetchData(`/DFe/ImprimirDanfeNFe?notaId=${notaId}`);

            const byteCharacters = atob(response.base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Falha na requisição:', err);
            alert('Erro na comunicação com o servidor.');
        }
    }

    const handleCancelarNF = async () => {
        const notasParaCancelar = notasSel.filter(
            (nota) => getDescricaoSituacaoNF(nota.situacaoNF) !== 'Cancelada',
        );

        try {
            const avisoRecebimentos = await obterAvisoRecebimentosVinculados(notasParaCancelar);
            if (avisoRecebimentos && !(await showYesNo(avisoRecebimentos))) return;
        } catch (error) {
            showMessage(`Erro ao verificar contas a receber vinculadas: ${error}`, 'danger');
            return;
        }

        notasSel.forEach((nota) => {
            if (getDescricaoSituacaoNF(nota.situacaoNF) === 'Cancelada') {
                showMessage(`Nota ${nota.numDoc} já foi cancelada!`, 'warning');
                return;
            }
            setNotaIdSelecionada(nota.id);
            setNotaSelecionada(nota);
            setShowModal(true);
        });
    };

    const handleCancelSuccess = () => {
        buscarNotas();
    };

    const handleVisualizarNF = () => {
        if (notasSel.length === 0) {
            showMessage('Selecione uma nota para visualizar!', 'warning');
            return;
        }
        if (notasSel.length > 1) {
            showMessage('Selecione apenas uma nota para visualizar!', 'warning');
            return;
        }
        setNotaSelecionada(notasSel[0]);
        setShowViewModal(true);
    };

    const handleEditarNF = async () => {
        if (notasSel.length === 0) {
            showMessage('Selecione uma nota para editar!', 'warning');
            return;
        }
        if (notasSel.length > 1) {
            showMessage('Selecione apenas uma nota para editar!', 'warning');
            return;
        }

        const resposta = await showYesNo(`Deseja realmente editar a nota ${notasSel[0].id}?`);

        if (resposta) {
            const nota = await buscaNotaById(notasSel[0].id);

            setNotaSel(nota);
            navigate('/pdv');
        }
    };

    return (
        <PageContainer>
            <h2 className="page-title">Pesquisar Notas Emitidas</h2>
            <PageHeader>
                <div className="group-div">
                    <Input
                        label="Data Inicial"
                        onChange={(e) => setPeriodoInicial(e.target.value)}
                        type="date"
                        size={120}
                        value={periodoInicial}
                    />
                    <Input
                        label="Hora Início"
                        onChange={(e) => setHoraInicial(e.target.value)}
                        type="time"
                        size={90}
                        value={horaInicial}
                    />
                    <Input
                        label="Data Final"
                        onChange={(e) => setPeriodoFinal(e.target.value)}
                        type="date"
                        size={120}
                        value={periodoFinal}
                    />
                    <Input
                        label="Hora Fim"
                        onChange={(e) => setHoraFinal(e.target.value)}
                        type="time"
                        size={90}
                        value={horaFinal}
                    />
                    <Input
                        label="Nome do Cliente"
                        placeholder="Digite o nome do cliente"
                        onChange={(e) => setCliente(e.target.value)}
                        value={cliente}
                        btnBuscar={handleBuscarCliente}
                        size={220}
                    />
                    <Input
                        label="Nº Doc."
                        value={numDoc}
                        onChange={(e) => setNumDoc(e.target.value)}
                        placeholder=""
                    />
                    <Select
                        label="Situação NF"
                        value={situacao}
                        onChange={(e) => setSituacao(e.target.value)}
                        options={[
                            { value: '', label: 'Todas' },
                            { value: -1, label: 'Autorizada/Recibo' },
                            { value: 2, label: 'Autorizada' },
                            { value: 9, label: 'Recibo' },
                            { value: 10, label: 'Pendente' },
                            { value: 4, label: 'Cancelada' },
                            { value: 3, label: 'Rejeitada' },
                        ]}
                        size={170}
                    />
                </div>
                <button
                    style={{ marginRight: '5px' }}
                    type="button"
                    className="btn btn-search"
                    onClick={buscarNotas}
                    disabled={disableBtn}
                >
                    Buscar
                </button>
                <div className="d-flex gap-2 align-items-end">
                    <button
                        type="button"
                        className="btn btn-outline-danger"
                        title="Abrir relatório de vendas em PDF"
                        disabled={disableBtn || !!gerandoRelatorio || notas.length === 0}
                        onClick={() => gerarRelatorio('pdf')}
                    >
                        <FaFilePdf /> {gerandoRelatorio === 'pdf' ? 'Gerando...' : 'PDF'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-success"
                        title="Baixar relatório de vendas em Excel"
                        disabled={disableBtn || !!gerandoRelatorio || notas.length === 0}
                        onClick={() => gerarRelatorio('excel')}
                    >
                        <FaFileExcel /> {gerandoRelatorio === 'excel' ? 'Gerando...' : 'Excel'}
                    </button>
                </div>
            </PageHeader>
            <LazyLoading
                items={notasOrdenadas}
                batchSize={1000}
                resetKey={`${resultadoVersao}|${ordenacao.campo || ''}|${ordenacao.direcao}`}
            >
                {({ containerRef, handleScroll, visibleItems }) => (
                    <PageContent containerRef={containerRef} onContainerScroll={handleScroll}>
                        <table className="page-content-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('id', 'Id', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('serie', 'Serie', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('numDoc', 'Num. Doc', { textAlign: 'right' })}
                            </th>
                            <th>{renderOrdenadorColuna('tipoMovDescricao', 'Tipo Mov.')}</th>
                            <th>{renderOrdenadorColuna('caixa', 'Caixa')}</th>
                            <th style={{ textAlign: 'center' }}>
                                {renderOrdenadorColuna('dataLanc', 'Data', { textAlign: 'center' })}
                            </th>
                            <th style={{ textAlign: 'center' }}>
                                {renderOrdenadorColuna('hora', 'Hora', { textAlign: 'center' })}
                            </th>
                            <th>{renderOrdenadorColuna('clienteDescricao', 'Cliente')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('vNota', 'Valor Nota', { textAlign: 'right' })}
                            </th>
                            <th>{renderOrdenadorColuna('situacaoDescricao', 'Sit. NF')}</th>
                            <th>{renderOrdenadorColuna('modeloNF', 'Modelo')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('cfop', 'CFOP', { textAlign: 'right' })}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleItems.map((nota) => (
                            <tr key={nota.id}>
                                <td>
                                    <input
                                        type="radio"
                                        name="notaSelecionada"
                                        checked={notasSel.some((n) => n.id === nota.id)}
                                        onChange={() => handleRadioChange(nota)}
                                    />
                                </td>
                                <td style={{ textAlign: 'right', minWidth: '60px' }}>{nota.id}</td>
                                <td style={{ textAlign: 'right', minWidth: '40px' }}>{nota.serie}</td>
                                <td style={{ textAlign: 'right', minWidth: '90px' }}>{nota.numDoc}</td>
                                <td style={{ minWidth: '100px' }}>{getDescricaoTipoMov(nota.tipoMov)}</td>
                                <td style={{ minWidth: '100px' }}>{nota.caixa?.descricao}</td>
                                <td style={{ textAlign: 'center', minWidth: '100px' }}>
                                    {formatDate(nota.dataLanc)}
                                </td>
                                <td style={{ textAlign: 'center', minWidth: '60px' }}>
                                    {formatTime(nota.dataLanc)}
                                </td>
                                <td style={{ minWidth: '300px' }}>
                                    {nota.clienteId} - {nota.clienteNome}
                                </td>
                                <td style={{ textAlign: 'right', minWidth: '100px' }}>
                                    {formatNumeric(nota.vNota)}
                                </td>
                                <td style={{ minWidth: '100px' }}>
                                    {getDescricaoSituacaoNF(nota.situacaoNF)}
                                </td>
                                <td style={{ textAlign: 'right', minWidth: '80px' }}>{nota.modeloNF}</td>
                                <td style={{ textAlign: 'right', minWidth: '80px' }}>{nota.cfop}</td>
                            </tr>
                        ))}
                    </tbody>
                        </table>
                    </PageContent>
                )}
            </LazyLoading>

            <PageFooter>
                <div className="botoes-footer">
                    {notasSel.length === 1 && notasSel[0].situacaoNF === 10 && (
                        <button onClick={handleEditarNF} className="btn btn-primary btn-footer">
                            Concluir NF
                        </button>
                    )}

                    <button onClick={handleImprimir} className="btn btn-secondary btn-footer">
                        Imprimir 2ª Via
                    </button>
                    {(() => {
                        const tipo = getTipoAcaoBotao();
                        return (
                            <button
                                onClick={handleAcaoNF}
                                className="btn btn-danger btn-footer"
                                disabled={tipo === 'DESABILITADO' || notasSel.length === 0}
                            >
                                {tipo === 'DESABILITADO' ? 'Cancelar/Excluir NF' : tipo}
                            </button>
                        );
                    })()}

                    <button onClick={handleVisualizarNF} className="btn btn-info btn-footer">
                        Visualizar NF
                    </button>
                    <button onClick={handleVisualizarNF} className="btn btn-warning btn-footer">
                        Devolver NF
                    </button>
                </div>

                <div className="totais-footer">
                    <strong>Total: </strong> {formatCurrency(calcularTotal())}
                </div>
            </PageFooter>

            <CancelarNFModal
                show={showModal}
                onHide={() => setShowModal(false)}
                notaId={notaIdSelecionada}
                modeloDoc={notaSelecionada?.modeloNF}
                situacaoNF={notaSelecionada?.situacaoNF}
                onCancelSuccess={handleCancelSuccess}
            />

            <ViewNotaModal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                notaId={notaSelecionada?.id}
            />
        </PageContainer>
    );
};

export default PesqNotasPage;
